import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Grid, CircularProgress, 
  Alert, Autocomplete, Step, Stepper, StepLabel, List, ListItemText,
  ListItemButton, Avatar, Chip, Divider
} from '@mui/material';
import {
  Search as SearchIcon, Person as PersonIcon, Business as BusinessIcon,
  Add as AddIcon, Cancel as CancelIcon, NavigateNext as NextIcon,
  NavigateBefore as BackIcon
} from '@mui/icons-material';
import { useLoading } from '../../context/LoadingContext';
import { useNotification } from '../../context/NotificationContext';

const steps = ['Select Company/Vendor', 'Select User', 'Confirm Details'];

function CreateBackgroundCheckDialog({ open, onClose, onSuccess, mode = 'create' }) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { startLoading } = useLoading();
  const { showNotification } = useNotification();

  // Step 1: Company/Vendor selection
  const [searchType, setSearchType] = useState('company'); // 'company' or 'vendor'
  const [companyOptions, setCompanyOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Step 2: User selection
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [existingBackgroundCheck, setExistingBackgroundCheck] = useState(null);

  // Step 3: Confirmation
  const [confirmationData, setConfirmationData] = useState({});

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Reset dialog state when opened/closed
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setError(null);
      setSelectedCompany(null);
      setSelectedVendor(null);
      setSelectedUser(null);
      setExistingBackgroundCheck(null);
      setUsers([]);
      fetchCompanyAndVendorOptions();
    }
  }, [open]);

  // Fetch company and vendor options
  const fetchCompanyAndVendorOptions = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const [companyRes, vendorRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/users/distinct/companies`, { headers }),
        fetch(`${process.env.REACT_APP_API_URL}/api/users/vendor-list`, { headers })
      ]);

      if (companyRes.ok) {
        const companies = await companyRes.json();
        setCompanyOptions(companies || []);
      }

      if (vendorRes.ok) {
        const vendors = await vendorRes.json();
        setVendorOptions(vendors || []);
      }
    } catch (err) {
      console.error('Error fetching options:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load company and vendor options.';
      setError(errorMessage);
      showNotification(`Error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for selected company/vendor
  const fetchUsers = async (companyName, vendorId = null) => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const params = new URLSearchParams();
      if (companyName) params.append('companyName', companyName);
      if (vendorId) params.append('vendorId', vendorId);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/by-company?${params.toString()}`,
        { headers }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const userData = await response.json();
      setUsers(userData || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load users for the selected company/vendor.';
      setError(errorMessage);
      showNotification(`Error: ${errorMessage}`, 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if user already has a background check
  const checkExistingBackgroundCheck = async (userId) => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/background-checks/by-user/${userId}`,
        { headers }
      );

      if (response.ok) {
        const existingCheck = await response.json();
        setExistingBackgroundCheck(existingCheck);
      } else {
        setExistingBackgroundCheck(null);
      }
    } catch (err) {
      console.error('Error checking existing background check:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to check existing background check.';
      showNotification(`Error: ${errorMessage}`, 'error');
      setExistingBackgroundCheck(null);
    }
  };

  // Handle company/vendor selection
  const handleCompanyVendorSelection = () => {
    if (searchType === 'company' && selectedCompany) {
      fetchUsers(selectedCompany);
      setActiveStep(1);
    } else if (searchType === 'vendor' && selectedVendor) {
      fetchUsers(selectedVendor.companyName, selectedVendor.vendorIdentifier);
      setActiveStep(1);
    } else {
      setError('Please select a company or vendor.');
    }
  };

  // Handle user selection
  const handleUserSelection = async (user) => {
    setSelectedUser(user);
    await checkExistingBackgroundCheck(user._id);
    
    // Prepare confirmation data
    setConfirmationData({
      user,
      company: searchType === 'company' ? selectedCompany : selectedVendor?.companyName,
      vendorId: searchType === 'vendor' ? selectedVendor?.vendorIdentifier : null,
      mode,
      hasExisting: !!existingBackgroundCheck
    });
    
    setActiveStep(2);
  };

  // Handle final confirmation and creation
  const handleConfirmCreate = async () => {
    const stopLoading = startLoading();
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const payload = {
        userId: selectedUser._id,
        personName: `${selectedUser.firstName} ${selectedUser.lastName}`,
        email: selectedUser.email,
        phone: selectedUser.phone,
        companyName: confirmationData.company,
        vendorId: confirmationData.vendorId,
        status: mode === 'request' ? 'Requested' : 'Not Started',
        dateRequested: mode === 'request' ? new Date().toISOString() : null,
        checkType: 'Comprehensive',
        address: selectedUser.address || {},
        assignedRoles: selectedUser.roles || {}
      };

      let endpoint, method;
      if (existingBackgroundCheck) {
        // Update existing record
        endpoint = `${process.env.REACT_APP_API_URL}/api/background-checks-master/${existingBackgroundCheck._id}`;
        method = 'PUT';
      } else {
        // Create new record
        endpoint = `${process.env.REACT_APP_API_URL}/api/v1/background-checks`;
        method = 'POST';
      }

      const response = await fetch(endpoint, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create/update background check');
      }

      const result = await response.json();
      showNotification(`Background check ${existingBackgroundCheck ? 'updated' : 'created'} successfully!`, 'success');
      onSuccess(result);
      onClose();
    } catch (err) {
      console.error('Error creating/updating background check:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create/update background check';
      setError(errorMessage);
      showNotification(`Error: ${errorMessage}`, 'error');
    } finally {
      stopLoading();
    }
  };

  // Handle step navigation
  const handleNext = () => {
    if (activeStep === 0) {
      handleCompanyVendorSelection();
    } else if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setError(null);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Search By</InputLabel>
                <Select
                  value={searchType}
                  onChange={(e) => {
                    setSearchType(e.target.value);
                    setSelectedCompany(null);
                    setSelectedVendor(null);
                  }}
                  label="Search By"
                >
                  <MenuItem value="company">Company Name</MenuItem>
                  <MenuItem value="vendor">Vendor ID</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {searchType === 'company' && (
              <Grid item xs={12}>
                <Autocomplete
                  options={companyOptions}
                  value={selectedCompany}
                  onChange={(event, newValue) => setSelectedCompany(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Company"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <BusinessIcon sx={{ mr: 1, color: 'action.active' }} />
                      }}
                    />
                  )}
                  loading={loading}
                  fullWidth
                />
              </Grid>
            )}

            {searchType === 'vendor' && (
              <Grid item xs={12}>
                <Autocomplete
                  options={vendorOptions}
                  value={selectedVendor}
                  onChange={(event, newValue) => setSelectedVendor(newValue)}
                  getOptionLabel={(option) => 
                    `${option.vendorIdentifier} - ${option.companyName || 'N/A'}`
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Vendor"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                      }}
                    />
                  )}
                  loading={loading}
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select User from {searchType === 'company' ? selectedCompany : selectedVendor?.companyName}
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : users.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ my: 4 }}>
                No users found for the selected {searchType}.
              </Typography>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {users.map((user) => (
                  <ListItemButton
                    key={user._id}
                    onClick={() => handleUserSelection(user)}
                    sx={{ border: 1, borderColor: 'divider', mb: 1, borderRadius: 1 }}
                  >
                    <Avatar sx={{ mr: 2 }}>
                      <PersonIcon />
                    </Avatar>
                    <ListItemText
                      primary={`${user.firstName} ${user.lastName}`}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          {user.phone && (
                            <Typography variant="body2" color="text.secondary">
                              {user.phone}
                            </Typography>
                          )}
                          {user.roles && user.roles.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              {user.roles.map((role, index) => (
                                <Chip
                                  key={index}
                                  label={role}
                                  size="small"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirm Background Check {mode === 'request' ? 'Request' : 'Creation'}
            </Typography>
            
            {existingBackgroundCheck && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This user already has a background check record. 
                {mode === 'request' ? 'Requesting' : 'Creating'} will update the existing record.
                {existingBackgroundCheck.documentPath && ' The previous document will be replaced when a new file is uploaded.'}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="User Name"
                  value={`${selectedUser?.firstName} ${selectedUser?.lastName}`}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  value={selectedUser?.email || ''}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company"
                  value={confirmationData.company || ''}
                  fullWidth
                  disabled
                />
              </Grid>
              {confirmationData.vendorId && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Vendor ID"
                    value={confirmationData.vendorId}
                    fullWidth
                    disabled
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Initial Status"
                  value={mode === 'request' ? 'Requested' : 'Not Started'}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Check Type"
                  value="Comprehensive"
                  fullWidth
                  disabled
                />
              </Grid>
            </Grid>

            {existingBackgroundCheck && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Existing Background Check Details:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Current Status"
                      value={existingBackgroundCheck.status || 'N/A'}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Date Requested"
                      value={existingBackgroundCheck.dateRequested ? 
                        new Date(existingBackgroundCheck.dateRequested).toLocaleDateString() : 'N/A'}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'request' ? 'Request Background Check' : 'Create Background Check'}
      </DialogTitle>
      
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack} startIcon={<BackIcon />}>
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={<NextIcon />}
            disabled={
              loading ||
              (activeStep === 0 && !selectedCompany && !selectedVendor) ||
              (activeStep === 1 && !selectedUser)
            }
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleConfirmCreate}
            variant="contained"
            startIcon={<AddIcon />}
            disabled={loading || !selectedUser}
          >
            {existingBackgroundCheck ? 'Update' : mode === 'request' ? 'Request' : 'Create'} Background Check
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CreateBackgroundCheckDialog;